import { logger } from '../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { timelineService, type TimelineState } from '../timeline/timeline-service.js'
import { addOverlayLayer, untrackOverlayLayer } from '../../components/map/layer-order-manager.js'
import {
  OM_MODELS,
  OM_LATEST_BASE_URL,
  OM_TILE_BASE_URL,
  type OmModelDef,
  type OmLayerDef,
} from './om-types.js'
import { selectBestModel, getModelsForVariable, getCandidateModels } from './om-model-selector.js'
import { readViewportField, readViewportFieldGaussian, type ViewportField } from './om/om-tiles.js'
import { OmCustomLayer, type OmField } from './om/om-custom-layer.js'
import { OmWindArrowLayer } from './om/om-wind-arrow-layer.js'
import { colormapForVariable, validRangeForVariable } from './om/om-colormap.js'
import { Capacitor } from '@capacitor/core'
import { environment } from '../../environments/environment.prod.js'

// `.om` tiles are served via the same-origin CloudFront `/om/*` rewrite on web. On
// native there's no such rewrite — the app's own host is the local Capacitor server —
// so route the (relative) `/om/data_spatial` path through the backend, the same host
// the APIs use. `OM_LATEST_BASE_URL` is already absolute (Open-Meteo), so it's fine.
const OM_TILE_ORIGIN = Capacitor.isNativePlatform() ? environment.api.baseUrl : ''

// The wind layer renders as a grid of animated direction arrows (boldness +
// colour = strength, gust = pulse) instead of a raster heatmap. Detected by its
// variable.
const ARROW_WIND_VARIABLE = 'wind_speed_10m'

const log = logger.create('om-layer')
const DEFAULT_OPACITY = 0.6

// .om format reserved Int16 NO-DATA sentinel (INT16_MAX, 0x7FFF) used for fill
// cells in regridded regional grids. Detected at the RAW (pre-scale) level so
// it's scale-independent. See updateField for the cross-validation against the
// @openmeteo oracle's NaN fill cells.
const OM_FILL_SENTINEL_RAW = 32767

// Minimal shape of `<model>/latest.json` that we consume.
interface OmLatestJson {
  reference_time: string
  valid_times: string[]
  crs_wkt?: string
}

interface OmLayerState {
  layerDef: OmLayerDef
  enabled: boolean
  opacity: number
  modelId: string | null // null = auto
  activeModelId: string | null
  layer: OmCustomLayer | OmWindArrowLayer | null
  layerId: string
  availableTimestamps: string[]
  referenceTime: string
  /** Model id that `referenceTime`/`availableTimestamps` were loaded for. Guards
   *  against using one model's run reference to build another model's URL. */
  referenceModelId: string | null
  selectedTimestamp: string
  metadataLoaded: boolean
  /** Grid bbox [latMin, lonMin, latMax, lonMax] of cell-centers, parsed from crs_wkt. */
  gridBbox: [number, number, number, number] | null
  /** Raw crs_wkt from latest.json; used to detect + build projected grids. */
  crsWkt: string | null
  /** Monotonic counter; results from stale generations are ignored. */
  loadGeneration: number
}

let map: maplibregl.Map | null = null
const layerStates = new Map<string, OmLayerState>()
let moveHandler: (() => void) | null = null
let moveDebounce: ReturnType<typeof setTimeout> | null = null

const HOUR_MS = 3600 * 1000
// How far back (hours) to expose past analysis (stepped by the model's output step).
const OM_PAST_HOURS = 24
// How many older runs to fall back through on a 404 (the latest run is often
// in-progress; older complete runs cover the same valid time at a higher lead).
const OM_RUN_FALLBACKS = 2

// Per-model run cadence + output step (hours), determined empirically from the OM
// archive. `runStep` = how often runs publish; `outStep` = spacing of valid times.
// Models present here support arbitrary-time look-back (past analysis from each
// hour's own run); models absent are forecast-only (latest run).
const OM_RUN_PARAMS: Record<string, { runStep: number; outStep: number }> = {
  ncep_hrrr_conus: { runStep: 1, outStep: 1 },
  metno_nordic_pp: { runStep: 1, outStep: 1 },
  ukmo_uk_deterministic_2km: { runStep: 1, outStep: 1 },
  dwd_icon_d2: { runStep: 3, outStep: 1 },
  dwd_icon_eu: { runStep: 3, outStep: 1 },
  jma_msm: { runStep: 3, outStep: 1 },
  meteofrance_arome_france0025: { runStep: 3, outStep: 1 },
  meteofrance_arome_france_hd: { runStep: 3, outStep: 1 },
  cmc_gem_hrdps: { runStep: 6, outStep: 1 },
  dwd_icon: { runStep: 6, outStep: 1 },
  ncep_gfs013: { runStep: 6, outStep: 1 },
  ecmwf_ifs025: { runStep: 6, outStep: 3 },
  ecmwf_ifs: { runStep: 6, outStep: 1 }, // 9km reduced-Gaussian (O1280), hourly output
}

const pad2 = (n: number): string => String(n).padStart(2, '0')
// Format a Date as the OM valid-time string ("YYYY-MM-DDTHH:00Z").
function fmtOmTime(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}:00Z`
}

function omUrl(modelId: string, run: Date, V: Date): string {
  const refPath = `${run.getUTCFullYear()}/${pad2(run.getUTCMonth() + 1)}/${pad2(run.getUTCDate())}/${pad2(run.getUTCHours())}00Z`
  const validFile = `${V.getUTCFullYear()}-${pad2(V.getUTCMonth() + 1)}-${pad2(V.getUTCDate())}T${pad2(V.getUTCHours())}00`
  return `${OM_TILE_ORIGIN}${OM_TILE_BASE_URL}/${modelId}/${refPath}/${validFile}.om`
}

// Build the `.om` URL, choosing the RUN by where the valid time V falls relative
// to the latest run R, so we can serve the recent PAST (not just R's forecast):
//   • V  > R   → latest run R forecasts V.
//   • V == R   → instantaneous: R's T+0; accumulation: prior cycle (no T+0 accum).
//   • V  < R   → freshest run covering V (floored to runStep); accumulation needs
//                a run ≤ V−outStep (the [V−outStep, V] sum).
// `runStepsBack` shifts to an older run for the 404/in-progress fallback. Models
// absent from OM_RUN_PARAMS are forecast-only (always the latest run).
function buildOmDataUrl(
  modelId: string,
  latestRef: string,
  validTime: string,
  isAccum: boolean,
  runStepsBack = 0
): string {
  const params = OM_RUN_PARAMS[modelId]
  const R = new Date(latestRef)
  if (!params) return omUrl(modelId, R, new Date(validTime))

  const { runStep, outStep } = params
  // Snap V to the output grid (aligned to 00Z).
  const V = new Date(
    Math.floor(new Date(validTime).getTime() / (outStep * HOUR_MS)) * (outStep * HOUR_MS)
  )

  let run: Date
  if (V.getTime() > R.getTime()) {
    run = R
  } else if (V.getTime() === R.getTime()) {
    run = isAccum ? new Date(R.getTime() - runStep * HOUR_MS) : R
  } else {
    const base = isAccum ? V.getTime() - outStep * HOUR_MS : V.getTime()
    run = new Date(Math.floor(base / (runStep * HOUR_MS)) * (runStep * HOUR_MS))
  }
  if (run.getTime() > R.getTime()) run = R
  if (runStepsBack > 0) run = new Date(run.getTime() - runStepsBack * runStep * HOUR_MS)
  return omUrl(modelId, run, V)
}

async function fetchMetadata(modelId: string): Promise<OmLatestJson | null> {
  try {
    const res = await fetch(`${OM_LATEST_BASE_URL}/${modelId}/latest.json`)
    if (!res.ok) return null
    return (await res.json()) as OmLatestJson
  } catch {
    return null
  }
}

// Parse the grid bbox from a CRS WKT string: regex `BBOX[latMin,lonMin,latMax,lonMax]`.
function parseGridBbox(crsWkt: string | undefined): [number, number, number, number] | null {
  if (!crsWkt) return null
  const m = crsWkt.match(/BBOX\[\s*([-\d.]+)\s*,\s*([-\d.]+)\s*,\s*([-\d.]+)\s*,\s*([-\d.]+)\s*\]/)
  if (!m) return null
  const vals = [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4])]
  if (vals.some(v => !Number.isFinite(v))) return null
  return [vals[0], vals[1], vals[2], vals[3]]
}

function getMapBounds(): [number, number, number, number] {
  if (!map) return [-90, -180, 90, 180]
  const b = map.getBounds()
  return [b.getSouth(), b.getWest(), b.getNorth(), b.getEast()]
}

// NaN-aware box blur over a row-major grid (index = row*nLon + col). Each output
// cell is the mean of the valid (non-NaN) cells in the (2r+1)² window; NaN cells
// stay NaN so genuine no-data regions are never filled and storms don't bleed past
// the data edge. Used to soften coarse categorical fields (WMO weather_code) — a
// cell with storm neighbours gains some of their value — without inventing data.
function boxBlurNaNAware(
  values: Float32Array,
  nLon: number,
  nLat: number,
  radius: number
): Float32Array {
  const out = new Float32Array(values.length)
  for (let row = 0; row < nLat; row++) {
    for (let col = 0; col < nLon; col++) {
      const ci = row * nLon + col
      if (Number.isNaN(values[ci])) {
        out[ci] = NaN
        continue
      }
      let sum = 0
      let count = 0
      for (let dr = -radius; dr <= radius; dr++) {
        const r = row + dr
        if (r < 0 || r >= nLat) continue
        for (let dc = -radius; dc <= radius; dc++) {
          const c = col + dc
          if (c < 0 || c >= nLon) continue
          const v = values[r * nLon + c]
          if (Number.isNaN(v)) continue
          sum += v
          count++
        }
      }
      out[ci] = count > 0 ? sum / count : values[ci]
    }
  }
  return out
}

function resolveModel(state: OmLayerState): OmModelDef | null {
  if (state.modelId) {
    return OM_MODELS.find(m => m.id === state.modelId) ?? null
  }
  return selectBestModel(getMapBounds(), state.layerDef.variable)
}

// Fetch + decode the variable for the current model/time and push it into the
// layer's OmCustomLayer (creating the layer on first use). Stale results
// (model/time changed mid-fetch) are discarded via the generation counter.
async function updateField(state: OmLayerState, model: OmModelDef, attempt = 0): Promise<void> {
  if (!map) return

  const gen = ++state.loadGeneration

  // Dual-resolution: e.g. the IFS layer reads 0.25° zoomed out and switches to
  // the 9km reduced-Gaussian zoomed in. Resolve the EFFECTIVE dataset for this zoom.
  let effId = model.id
  let gaussian = false
  if (model.hiRes && map.getZoom() >= model.hiRes.minZoom) {
    effId = model.hiRes.modelId
    gaussian = model.hiRes.gaussian ?? false
  }

  // Guard: referenceTime/availableTimestamps/crsWkt must belong to the EFFECTIVE
  // dataset. After a model OR resolution switch they may be stale (using one
  // run's reference to build another's URL 404s). Reload the run metadata if so.
  if (state.referenceModelId !== effId) {
    await loadTimestamps(state, effId)
    if (gen !== state.loadGeneration) return // superseded during reload
  }
  if (!state.selectedTimestamp || !state.referenceTime) return

  // attempt 0 = normal; attempt 1 = after a latest.json refresh; attempt ≥2 =
  // fall back to progressively older runs (the latest run is often in-progress).
  const url = buildOmDataUrl(
    effId,
    state.referenceTime,
    state.selectedTimestamp,
    isAccumulationVariable(state.layerDef.variable),
    Math.max(0, attempt - 1)
  )
  const gridBbox: [number, number, number, number] = state.gridBbox ??
    model.bbox ?? [-90, -180, 90, 180]

  // Read the visible band(s). Gaussian datasets (IFS 9km) use the reduced-Gaussian
  // reader (windows + resamples to a regular sub-grid); everything else uses the
  // rectangular strip reader. wind_speed_10m isn't stored — derive it from the u/v
  // components (works on either reader).
  const readOpts = { viewport: getMapBounds(), gridBbox, crsWkt: state.crsWkt }
  const readField = (vurl: string, vvar: string): Promise<ViewportField | null> =>
    gaussian
      ? readViewportFieldGaussian(vurl, vvar, readOpts)
      : readViewportField(vurl, vvar, readOpts)
  let geom: ViewportField | null = null
  let windU: ViewportField | null = null
  let windV: ViewportField | null = null
  let windGust: ViewportField | null = null
  const isWind = state.layerDef.variable === ARROW_WIND_VARIABLE
  try {
    if (isWind) {
      // Wind renders as direction arrows: read the u/v vector
      // (direction + sustained speed) and gusts (the surge magnitude — best-
      // effort, some models lack it). wind_speed_10m itself is never stored.
      const [u, v, g] = await Promise.all([
        readField(url, 'wind_u_component_10m'),
        readField(url, 'wind_v_component_10m'),
        readField(url, 'wind_gusts_10m').catch(() => null),
      ])
      geom = u
      windU = u
      windV = v
      windGust = g
    } else {
      geom = await readField(url, state.layerDef.variable)
    }
  } catch (err) {
    if (attempt === 0) {
      // The run may have rotated (Open-Meteo deletes superseded runs) → refresh
      // latest.json, re-snap the time, and retry against the current run.
      log.info(`om fetch failed for ${state.layerDef.id} (${effId}); refreshing run + retrying`)
      state.referenceModelId = null
      await loadTimestamps(state, effId)
      if (gen !== state.loadGeneration) return
      state.selectedTimestamp = snapToTimestamp(
        timelineService.getState().currentTime,
        state.availableTimestamps
      )
      return updateField(state, model, 1)
    }
    if (attempt <= OM_RUN_FALLBACKS) {
      // The latest run is likely still in-progress (files not written yet) →
      // fall back to an older, complete run that covers this valid time.
      return updateField(state, model, attempt + 1)
    }
    log.warn(
      `om fetch failed for ${state.layerDef.id} (${model.id}) after ${attempt} attempts: ${String(err)}`
    )
    return
  }

  // Discard stale results (model/time changed, or layer disabled, mid-fetch).
  if (!map || !state.enabled || gen !== state.loadGeneration) return
  if (!geom) return // grid not in the current viewport

  // Wind: direction-arrow grid driven by the raw u/v/gust fields (the layer
  // resamples + uploads them itself; no OmField derivation).
  if (isWind) {
    if (state.layer instanceof OmWindArrowLayer) {
      state.layer.setField(windU, windV, windGust)
      state.layer.setOpacity(state.opacity)
      return
    }
    const layer = new OmWindArrowLayer({
      id: state.layerId,
      u: windU,
      v: windV,
      gust: windGust,
      opacity: state.opacity,
    })
    state.layer = layer
    addOverlayLayer(map, layer as unknown as maplibregl.LayerSpecification, state.layerDef.id)
    return
  }

  // Physical Float32 values: map raw->scaled and mask the INT16_MAX sentinel +
  // out-of-physical-range fill (some fields, e.g. snowfall_height, carry
  // non-sentinel garbage fill).
  const validRange = validRangeForVariable(state.layerDef.variable)
  // Optional per-layer remap onto a shared scale (e.g. the unified lightning index).
  // Applied after sentinel/range masking; a NaN result (e.g. a non-thunderstorm WMO
  // code) renders transparent via the shader's isnan path.
  const valueTransform = state.layerDef.valueTransform
  const values = new Float32Array(geom.values.length)
  for (let i = 0; i < geom.values.length; i++) {
    if (geom.values[i] === OM_FILL_SENTINEL_RAW) {
      values[i] = NaN
      continue
    }
    const x = geom.values[i] / geom.scale
    const masked = validRange && (x < validRange[0] || x > validRange[1]) ? NaN : x
    values[i] = valueTransform && !Number.isNaN(masked) ? valueTransform(masked) : masked
  }

  // Optional NaN-aware spatial smoothing for coarse categorical layers (WMO
  // weather_code): a cell flanked by storm cells picks up some of their value, so
  // the discrete 13 km blocks read as a softer potential field. Skipped for
  // continuous layers (no smoothRadius) so real LPI/HRRR detail is preserved.
  const smoothRadius = state.layerDef.smoothRadius ?? 0
  const blurred =
    smoothRadius > 0 ? boxBlurNaNAware(values, geom.nLon, geom.nLat, smoothRadius) : values

  // row0South is always true (the OM convention; om-tiles georeferences the band
  // accordingly). The band carries its own sub-bbox / shifted projection origin.
  const field: OmField = {
    values: blurred,
    nLon: geom.nLon,
    nLat: geom.nLat,
    bbox: geom.bbox,
    row0South: geom.row0South,
    ...(geom.projection ? { projection: geom.projection } : {}),
  }

  if (state.layer instanceof OmCustomLayer) {
    state.layer.setField(field)
    state.layer.setOpacity(state.opacity)
    return
  }

  // First load: create the custom layer and add it via the order manager so it
  // lands in the correct tier / z-order group. addOverlayLayer accepts any
  // LayerSpecification; CustomLayerInterface is added through the same path
  // (cast required because the manager's signature is typed to LayerSpecification).
  const layer = new OmCustomLayer({
    id: state.layerId,
    field,
    colormap: state.layerDef.colormap ?? colormapForVariable(state.layerDef.variable),
    opacity: state.opacity,
  })
  state.layer = layer
  addOverlayLayer(map, layer as unknown as maplibregl.LayerSpecification, state.layerDef.id)
}

function removeLayer(state: OmLayerState): void {
  if (map && state.layer && map.getLayer(state.layerId)) {
    map.removeLayer(state.layerId)
  }
  untrackOverlayLayer(state.layerId)
  state.layer = null
}

// Accumulation fields (precipitation etc.) are ABSENT from the T+0 file (valid ==
// run reference): a model has no accumulation at its analysis instant — they
// first appear at T+1 (the [R, R+1] sum). Instantaneous fields (CAPE, temp, wind,
// cloud, …) ARE present at T+0. So for accumulation layers we drop the run-
// reference timestamp; requesting that file would 404 (variable not in it).
function isAccumulationVariable(variable: string): boolean {
  return (
    variable === 'precipitation' ||
    variable === 'rain' ||
    variable === 'showers' ||
    variable === 'snowfall' ||
    variable === 'snowfall_water_equivalent'
  )
}

async function loadTimestamps(state: OmLayerState, modelId: string): Promise<void> {
  const meta = await fetchMetadata(modelId)
  if (!meta?.valid_times) return
  const params = OM_RUN_PARAMS[modelId]
  let validTimes = meta.valid_times
  if (params && meta.reference_time) {
    // Look-back models: prepend recent PAST valid times (on the output grid), each
    // sourced from its own run (see buildOmDataUrl). This is what lets the timeline
    // go before the latest run's start.
    const R = new Date(meta.reference_time)
    const past: string[] = []
    for (let h = OM_PAST_HOURS; h >= params.outStep; h -= params.outStep) {
      past.push(fmtOmTime(new Date(R.getTime() - h * HOUR_MS)))
    }
    validTimes = [...past, ...validTimes]
  } else if (
    isAccumulationVariable(state.layerDef.variable) &&
    validTimes.length > 1 &&
    meta.reference_time &&
    new Date(validTimes[0]).getTime() === new Date(meta.reference_time).getTime()
  ) {
    // No past support for this model: just drop the empty accumulation T+0.
    validTimes = validTimes.slice(1)
  }
  state.availableTimestamps = validTimes
  state.referenceTime = meta.reference_time
  state.referenceModelId = modelId
  // Grid bbox parsed from crs_wkt (used to georeference the OmField).
  state.gridBbox = parseGridBbox(meta.crs_wkt)
  state.crsWkt = meta.crs_wkt ?? null
  state.metadataLoaded = true
  if (!state.selectedTimestamp && state.availableTimestamps.length > 0) {
    const tlState = timelineService.getState()
    state.selectedTimestamp = snapToTimestamp(tlState.currentTime, state.availableTimestamps)
  }
}

function snapToTimestamp(currentTime: Date, timestamps: string[]): string {
  if (timestamps.length === 0) return ''
  const target = currentTime.getTime()
  let best = timestamps[0]
  let bestDiff = Math.abs(new Date(best).getTime() - target)
  for (let i = 1; i < timestamps.length; i++) {
    const diff = Math.abs(new Date(timestamps[i]).getTime() - target)
    if (diff < bestDiff) {
      bestDiff = diff
      best = timestamps[i]
    }
  }
  return best
}

function onTimelineChange(tlState: Readonly<TimelineState>): void {
  for (const state of layerStates.values()) {
    if (!state.enabled || state.availableTimestamps.length === 0) continue
    const ts = snapToTimestamp(tlState.currentTime, state.availableTimestamps)
    if (ts && ts !== state.selectedTimestamp) {
      state.selectedTimestamp = ts
      const model = resolveModel(state)
      if (model) void updateField(state, model)
    }
  }
}

function onViewportChange(): void {
  for (const state of layerStates.values()) {
    if (!state.enabled) continue
    const model = resolveModel(state)
    if (!model) continue
    if (state.modelId === null && model.id !== state.activeModelId) {
      // Auto-select switched models for this viewport — reload the run, re-window.
      state.activeModelId = model.id
      void loadTimestamps(state, model.id).then(() => {
        if (state.enabled) void updateField(state, model)
      })
    } else {
      // Re-window to the new viewport (the set of visible strips changed). Strips
      // already loaded for this run are served from the session/edge cache.
      void updateField(state, model)
    }
  }
}

// Open-Meteo publishes new model runs continuously (HRRR hourly) and deletes old
// ones. Re-check latest.json periodically; when the run has advanced, re-snap the
// selected time and re-render so a layer never stays stuck on a stale run's
// (possibly short) horizon — e.g. a non-extended HRRR run only reaches +18h.
const RUN_REFRESH_MS = 5 * 60 * 1000
let runRefreshTimer: ReturnType<typeof setInterval> | null = null

async function refreshRuns(): Promise<void> {
  for (const state of layerStates.values()) {
    if (!state.enabled || !state.activeModelId) continue
    const model = OM_MODELS.find(m => m.id === state.activeModelId)
    if (!model) continue
    const prevRef = state.referenceTime
    await loadTimestamps(state, model.id)
    if (state.referenceTime && state.referenceTime !== prevRef) {
      log.info(`om: ${model.id} run advanced ${prevRef} -> ${state.referenceTime}`)
      state.selectedTimestamp = snapToTimestamp(
        timelineService.getState().currentTime,
        state.availableTimestamps
      )
      void updateField(state, model)
    }
  }
}

function attachMoveListener(): void {
  if (!map || moveHandler) return
  moveHandler = () => {
    if (moveDebounce) clearTimeout(moveDebounce)
    moveDebounce = setTimeout(onViewportChange, 300)
  }
  map.on('moveend', moveHandler)
  map.on('zoomend', moveHandler)
}

function detachMoveListener(): void {
  if (!map || !moveHandler) return
  map.off('moveend', moveHandler)
  map.off('zoomend', moveHandler)
  moveHandler = null
  if (moveDebounce) {
    clearTimeout(moveDebounce)
    moveDebounce = null
  }
}

// Any enabled OM layer needs the move listener now: windowing re-fetches the
// visible strips on pan/zoom (not just auto-mode model re-selection).
function hasAnyEnabledLayer(): boolean {
  for (const state of layerStates.values()) {
    if (state.enabled) return true
  }
  return false
}

// ---- Public API ----

export function registerOmLayers(mapInstance: maplibregl.Map): void {
  map = mapInstance
  timelineService.addListener(onTimelineChange)
  if (!runRefreshTimer) runRefreshTimer = setInterval(() => void refreshRuns(), RUN_REFRESH_MS)
}

export function unregisterOmLayers(): void {
  timelineService.removeListener(onTimelineChange)
  if (runRefreshTimer) {
    clearInterval(runRefreshTimer)
    runRefreshTimer = null
  }
  detachMoveListener()
  for (const state of layerStates.values()) {
    if (state.enabled) removeLayer(state)
  }
  layerStates.clear()
  map = null
}

export function enableOmLayer(layerDef: OmLayerDef): void {
  if (!map) return
  let state = layerStates.get(layerDef.id)
  if (!state) {
    state = {
      layerDef,
      enabled: false,
      opacity: DEFAULT_OPACITY,
      modelId: null,
      activeModelId: null,
      layer: null,
      layerId: `om-lyr-${layerDef.id}`,
      availableTimestamps: [],
      referenceTime: '',
      referenceModelId: null,
      selectedTimestamp: '',
      metadataLoaded: false,
      gridBbox: null,
      crsWkt: null,
      loadGeneration: 0,
    }
    layerStates.set(layerDef.id, state)
  }

  if (state.enabled) return
  state.enabled = true

  const model = resolveModel(state)
  if (!model) {
    log.warn(`No model available for ${layerDef.variable}`)
    return
  }

  state.activeModelId = model.id
  void loadTimestamps(state, model.id).then(() => {
    if (!state.enabled) return
    void updateField(state, model)
  })

  if (hasAnyEnabledLayer()) attachMoveListener()
}

export function disableOmLayer(layerId: string): void {
  const state = layerStates.get(layerId)
  if (!state?.enabled) return
  state.enabled = false
  state.loadGeneration++ // invalidate any in-flight fetch
  removeLayer(state)
  if (!hasAnyEnabledLayer()) detachMoveListener()
}

export function toggleOmLayer(layerDef: OmLayerDef): void {
  const state = layerStates.get(layerDef.id)
  if (state?.enabled) {
    disableOmLayer(layerDef.id)
  } else {
    enableOmLayer(layerDef)
  }
}

export function isOmLayerEnabled(layerId: string): boolean {
  return layerStates.get(layerId)?.enabled ?? false
}

export function setOmLayerOpacity(layerId: string, opacity: number): void {
  const state = layerStates.get(layerId)
  if (!state) return
  state.opacity = Math.max(0, Math.min(1, opacity))
  state.layer?.setOpacity(state.opacity)
}

export function getOmLayerOpacity(layerId: string): number {
  return layerStates.get(layerId)?.opacity ?? DEFAULT_OPACITY
}

export function setOmLayerModel(layerId: string, modelId: string | null): void {
  const state = layerStates.get(layerId)
  if (!state) return

  state.modelId = modelId
  if (!state.enabled) return

  const model = resolveModel(state)
  if (!model) return

  if (model.id !== state.activeModelId) {
    state.activeModelId = model.id
    state.metadataLoaded = false
    void loadTimestamps(state, model.id).then(() => {
      if (!state.enabled) return
      void updateField(state, model)
    })
  }

  if (hasAnyEnabledLayer()) attachMoveListener()
  else detachMoveListener()
}

export function getOmLayerModelId(layerId: string): string | null {
  return layerStates.get(layerId)?.modelId ?? null
}

export function getOmLayerActiveModelId(layerId: string): string | null {
  return layerStates.get(layerId)?.activeModelId ?? null
}

export function getAvailableModels(layerDef: OmLayerDef): OmModelDef[] {
  return getModelsForVariable(layerDef.variable)
}

// Models selectable for this layer at the CURRENT viewport (global + regionals
// containing the view), best-first. This is the set the Key's model dropdown
// should offer — not the whole catalogue.
export function getViewportModels(layerDef: OmLayerDef): OmModelDef[] {
  return getCandidateModels(layerDef.variable, getMapBounds())
}

export function reAddOmLayers(): void {
  for (const state of layerStates.values()) {
    if (!state.enabled) continue
    const model = resolveModel(state)
    if (!model) continue
    // After a style reload the custom layer is gone from the map; recreate it.
    if (!map?.getLayer(state.layerId)) {
      state.layer = null
      void updateField(state, model)
    }
  }
}
